import React from "react";
import styled from "@emotion/styled";
import { useField } from "@imminently/decisively-ui/Form";
import Button from "@imminently/decisively-ui/Button";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@imminently/decisively-ui/Icon";
import Popover from "@mui/material/Popover";
import copy from "copy-to-clipboard";
import { scrollableMixin } from "./scrollbar";
import { useTranslation } from "react-i18next";
import MonacoEditor, { DiffEditor } from "@monaco-editor/react";
import { DecisionGraph, formatterSuggestions } from "@imminently/decisively-core";

const TEMPLATE_MARKER_OWNER = "json-editor-template-markers";
const PARENT_TEMPLATE_PREFIX = "@parent";
/** Every formatter the server understands, from decisively-core's registry. */
const DEFAULT_TEMPLATE_FORMATTERS = formatterSuggestions();

type OffsetRange = {
  start: number;
  end: number;
};

type TemplateCompletionContext = {
  mode: "attribute" | "formatter";
  searchTerm: string;
  replaceStart: number;
};

type TemplateDiagnostic = {
  startOffset: number;
  endOffset: number;
  message: string;
};

export const getJsonPropertyLine = (text: string, path: string): number => {
  const property = path.split(".").filter(Boolean).at(-1);
  if (!property) return 1;
  const escapedProperty = property.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const propertyPattern = new RegExp(`^\\s*"${escapedProperty}"\\s*:`);
  const index = text.split("\n").findIndex((line) => propertyPattern.test(line));
  return index >= 0 ? index + 1 : 1;
};

type TemplateAttributeSuggestionsArgs = {
  searchTerm: string;
  attributes: string[];
  entities: string[];
  attributesByEntity: Map<string, string[]>;
};

const normalizeUniqueStrings = (input: unknown[]): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const it of input) {
    if (typeof it !== "string") continue;
    const v = it.trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
};

const getJsonStringRanges = (text: string): OffsetRange[] => {
  const ranges: OffsetRange[] = [];
  let inString = false;
  let escaped = false;
  let start = 0;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (!inString) {
      if (ch === "\"") {
        inString = true;
        escaped = false;
        start = i + 1;
      }
      continue;
    }

    if (escaped) {
      escaped = false;
      continue;
    }

    if (ch === "\\") {
      escaped = true;
      continue;
    }

    if (ch === "\"") {
      ranges.push({ start, end: i });
      inString = false;
    }
  }

  if (inString) {
    ranges.push({ start, end: text.length });
  }

  return ranges;
};

const getStringRangeAtOffset = (text: string, offset: number): OffsetRange | null => {
  const ranges = getJsonStringRanges(text);
  for (const range of ranges) {
    if (offset >= range.start && offset <= range.end) {
      return range;
    }
  }
  return null;
};

const getTemplateCompletionContext = (text: string, offset: number): TemplateCompletionContext | null => {
  const range = getStringRangeAtOffset(text, offset);
  if (!range) return null;

  const textInStringUpToCursor = text.slice(range.start, offset);
  const openIdx = textInStringUpToCursor.lastIndexOf("{{");
  if (openIdx < 0) return null;

  const closeIdx = textInStringUpToCursor.lastIndexOf("}}");
  if (closeIdx > openIdx) return null;

  const templateStart = range.start + openIdx;
  const contentBeforeCursor = textInStringUpToCursor.slice(openIdx + 2);

  const lastPipeIdx = contentBeforeCursor.lastIndexOf("|");

  if (lastPipeIdx < 0) {
    const leadingWhitespace = contentBeforeCursor.length - contentBeforeCursor.trimStart().length;
    return {
      mode: "attribute",
      searchTerm: contentBeforeCursor.trim(),
      replaceStart: templateStart + 2 + leadingWhitespace,
    };
  }

  const currentFormatterChunk = contentBeforeCursor.slice(lastPipeIdx + 1);
  const leadingWhitespace = currentFormatterChunk.length - currentFormatterChunk.trimStart().length;
  return {
    mode: "formatter",
    searchTerm: currentFormatterChunk.trim(),
    replaceStart: templateStart + 2 + lastPipeIdx + 1 + leadingWhitespace,
  };
};

const findTemplateDiagnostics = (
  text: string,
  attributes: string[],
  entities: string[],
  attributesByEntity: Map<string, string[]>,
): TemplateDiagnostic[] => {
  const diagnostics: TemplateDiagnostic[] = [];

  for (const stringRange of getJsonStringRanges(text)) {
    const stringText = text.slice(stringRange.start, stringRange.end);
    let cursor = 0;

    while (cursor < stringText.length) {
      const openIdx = stringText.indexOf("{{", cursor);
      if (openIdx < 0) break;

      const closeIdx = stringText.indexOf("}}", openIdx + 2);
      const absoluteOpen = stringRange.start + openIdx;

      if (closeIdx < 0) {
        diagnostics.push({
          startOffset: absoluteOpen,
          endOffset: absoluteOpen + 2,
          message: "Unclosed template expression",
        });
        break;
      }

      const rawTemplate = stringText.slice(openIdx + 2, closeIdx);
      const firstPipe = rawTemplate.indexOf("|");
      const rawAttribute = firstPipe >= 0 ? rawTemplate.slice(0, firstPipe) : rawTemplate;
      const trimmedAttribute = rawAttribute.trim();
      const leadingWhitespace = rawAttribute.length - rawAttribute.trimStart().length;
      const attrStart = absoluteOpen + 2 + leadingWhitespace;
      const attrEnd = attrStart + Math.max(trimmedAttribute.length, 1);

      if (!trimmedAttribute) {
        diagnostics.push({
          startOffset: absoluteOpen,
          endOffset: absoluteOpen + (closeIdx - openIdx + 2),
          message: "Template attribute is missing",
        });
      } else if (trimmedAttribute.startsWith(PARENT_TEMPLATE_PREFIX)) {
        const parentError = getTemplateParentValidationError(trimmedAttribute, entities, attributesByEntity);
        if (parentError) {
          diagnostics.push({
            startOffset: attrStart,
            endOffset: attrEnd,
            message: parentError,
          });
        }
      } else if (attributes.length > 0) {
        const hasAttribute = attributes.some((attribute) => attribute.toLowerCase() === trimmedAttribute.toLowerCase());
        if (!hasAttribute) {
          diagnostics.push({
            startOffset: attrStart,
            endOffset: attrEnd,
            message: `Unknown template attribute "${trimmedAttribute}"`,
          });
        }
      }

      cursor = closeIdx + 2;
    }
  }

  return diagnostics;
};

const getCaseInsensitiveMatch = (items: string[], value: string): string | undefined => {
  const lowerValue = value.toLowerCase();
  return items.find((item) => item.toLowerCase() === lowerValue);
};

const getCaseInsensitiveMapKey = (map: Map<string, unknown>, key: string): string | undefined => {
  const lowerKey = key.toLowerCase();
  for (const existingKey of map.keys()) {
    if (existingKey.toLowerCase() === lowerKey) return existingKey;
  }
  return undefined;
};

const getTemplateParentValidationError = (
  attribute: string,
  entities: string[],
  attributesByEntity: Map<string, string[]>,
): string | undefined => {
  const trimmed = attribute.trim();
  if (trimmed === PARENT_TEMPLATE_PREFIX) {
    return "Use @parent/<entity>/<attribute>";
  }
  if (!trimmed.startsWith(`${PARENT_TEMPLATE_PREFIX}/`)) {
    return "Invalid @parent format. Use @parent/<entity>/<attribute>";
  }

  const path = trimmed.slice(PARENT_TEMPLATE_PREFIX.length + 1);
  const separator = path.indexOf("/");
  if (separator < 0) {
    return "Missing attribute path after @parent/<entity>/";
  }

  const rawEntity = path.slice(0, separator).trim();
  const rawAttribute = path.slice(separator + 1).trim();

  if (!rawEntity) {
    return "Missing entity name after @parent/";
  }
  if (!rawAttribute) {
    return `Missing attribute path for @parent/${rawEntity}/`;
  }

  const matchedEntity =
    getCaseInsensitiveMatch(entities, rawEntity) ??
    getCaseInsensitiveMapKey(attributesByEntity, rawEntity);
  if (!matchedEntity) {
    return `Unknown @parent entity "${rawEntity}"`;
  }

  const scopedAttributes = attributesByEntity.get(matchedEntity) ?? [];
  if (!getCaseInsensitiveMatch(scopedAttributes, rawAttribute)) {
    return `Unknown attribute "${rawAttribute}" on entity "${matchedEntity}"`;
  }

  return undefined;
};

const getTemplateAttributeSuggestions = ({
  searchTerm,
  attributes,
  entities,
  attributesByEntity,
}: TemplateAttributeSuggestionsArgs): string[] => {
  const trimmed = searchTerm.trim();
  const lower = trimmed.toLowerCase();
  const parentPrefix = PARENT_TEMPLATE_PREFIX.toLowerCase();

  if (!lower.startsWith("@")) {
    return attributes
      .filter((item) => item.toLowerCase().includes(lower))
      .sort((a, b) => {
        const scoreA = scoreSuggestion(a, trimmed);
        const scoreB = scoreSuggestion(b, trimmed);
        if (scoreA !== scoreB) return scoreA - scoreB;
        return a.localeCompare(b);
      });
  }

  if (parentPrefix.startsWith(lower) || lower.startsWith(parentPrefix)) {
    if (!lower.startsWith(`${parentPrefix}/`)) {
      return `${PARENT_TEMPLATE_PREFIX}/`.toLowerCase().includes(lower)
        ? [`${PARENT_TEMPLATE_PREFIX}/`]
        : [];
    }

    const remainder = trimmed.slice(PARENT_TEMPLATE_PREFIX.length + 1);
    const separator = remainder.indexOf("/");
    if (separator < 0) {
      const entityTerm = remainder.trim();
      return entities
        .filter((entity) => entity.toLowerCase().includes(entityTerm.toLowerCase()))
        .sort((a, b) => {
          const scoreA = scoreSuggestion(a, entityTerm);
          const scoreB = scoreSuggestion(b, entityTerm);
          if (scoreA !== scoreB) return scoreA - scoreB;
          return a.localeCompare(b);
        })
        .map((entity) => `${PARENT_TEMPLATE_PREFIX}/${entity}/`);
    }

    const rawEntity = remainder.slice(0, separator).trim();
    const attributeTerm = remainder.slice(separator + 1).trim();
    const matchedEntity = getCaseInsensitiveMatch(entities, rawEntity) ?? getCaseInsensitiveMapKey(attributesByEntity, rawEntity);

    if (!matchedEntity) {
      return entities
        .filter((entity) => entity.toLowerCase().includes(rawEntity.toLowerCase()))
        .sort((a, b) => {
          const scoreA = scoreSuggestion(a, rawEntity);
          const scoreB = scoreSuggestion(b, rawEntity);
          if (scoreA !== scoreB) return scoreA - scoreB;
          return a.localeCompare(b);
        })
        .map((entity) => `${PARENT_TEMPLATE_PREFIX}/${entity}/`);
    }

    const scopedAttributes = attributesByEntity.get(matchedEntity) ?? [];
    return scopedAttributes
      .filter((attribute) => attribute.toLowerCase().includes(attributeTerm.toLowerCase()))
      .sort((a, b) => {
        const scoreA = scoreSuggestion(a, attributeTerm);
        const scoreB = scoreSuggestion(b, attributeTerm);
        if (scoreA !== scoreB) return scoreA - scoreB;
        return a.localeCompare(b);
      })
      .map((attribute) => `${PARENT_TEMPLATE_PREFIX}/${matchedEntity}/${attribute}`);
  }

  return [];
};

const scoreSuggestion = (candidate: string, searchTerm: string): number => {
  if (!searchTerm) return 0;
  const lcCandidate = candidate.toLowerCase();
  const lcSearch = searchTerm.toLowerCase();
  if (lcCandidate.startsWith(lcSearch)) return 0;
  const containsIdx = lcCandidate.indexOf(lcSearch);
  if (containsIdx >= 0) return containsIdx + 1;
  return Number.MAX_SAFE_INTEGER;
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  root: {
    "& .MuiPopover-paper": {
      color: "var(--destructive)",
      padding: "0.25rem 0.5rem",
    },
  },
}));

const Wrap = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .adapter-json-guidance-line {
    background: color-mix(in srgb, var(--primary) 18%, transparent);
    border-left: 3px solid var(--primary);
  }
`;

const Toolbar = styled.div`
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  z-index: 1;
  opacity: 0.8;
  display: flex;
  background: var(--card);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s ease-in-out;

  &:hover, &:focus, &:active, &:focus-within {
    opacity: 1;
  }
`;

const InnerWrap = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  ${scrollableMixin};

  > * {
    flex: 1;
  }
`;

const StyledIconButton = styled(Button)`
  padding: 8px;
  border-radius: 0;

  & .MuiSvgIcon-root {
    opacity: 0.75;
    width: 1.25rem;
    height: 1.25rem;
  }

  &.hidden {
    display: none;
  }
`;

// Editor options JsonEditor and JsonDiffEditor share.
const BASE_EDITOR_OPTIONS = {
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  lineNumbers: "on",
  unicodeHighlight: {
    includeStrings: true,
    invisibleCharacters: true,
    ambiguousCharacters: true,
  },
  scrollbar: { vertical: "visible", horizontal: "visible" },
  fontSize: 14,
  wordWrap: "off",
  automaticLayout: true,
} as const;

const CopyToCbdBtn: React.FC<{ v: string; withBeautify?: boolean }> = React.memo(({ v, withBeautify }) => {
  const [tooltipActive, setTooltipActive] = React.useState(false);
  const onCopy = React.useCallback(() => {
    copy(v);
    setTooltipActive(true);
  }, [v]);
  const handleTooltipClose = React.useCallback(() => setTooltipActive(false), []);

  return (
    <Tooltip
      open={tooltipActive}
      title="Copied to clipboard!"
      leaveDelay={800}
      onClose={handleTooltipClose}
    >
      <StyledIconButton
        label="Copy JSON"
        onClick={onCopy}
        className={withBeautify ? "withBeautify" : undefined}
      >
        <Icon name="copy" />
      </StyledIconButton>
    </Tooltip>
  );
});
CopyToCbdBtn.displayName = "JsonEditor/CopyToCbdBtn";

export interface JsonEditorProps {
  name?: string;
  label?: string;
  value?: string;
  defaultValue?: string;
  resetKey?: string | number;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  withCopy?: boolean;
  readOnly?: boolean;
  useDescription?: boolean;
  graph?: any;
  templateAware?: boolean;
  templateAttributes?: string[];
  templateFormatters?: string[];
  templateEntities?: string[];
  revealPath?: string;
}

export const JsonEditor: React.FC<JsonEditorProps> = React.memo((props) => {
  const {
    name,
    label,
    value: propValue,
    defaultValue = "",
    resetKey,
    onChange,
    className,
    disabled,
    withCopy,
    readOnly,
    useDescription,
    graph,
    templateAware = false,
    templateAttributes,
    templateFormatters,
    templateEntities,
    revealPath,
  } = props;
  const { t } = useTranslation();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const editorRef = React.useRef<any>(null);
  const monacoRef = React.useRef<any>(null);
  const completionProviderRef = React.useRef<{ dispose: () => void } | null>(null);
  const guidanceDecorationsRef = React.useRef<string[]>([]);
  const revealPathRef = React.useRef(revealPath);
  const templateAwareRef = React.useRef(false);
  const templateAttributesRef = React.useRef<string[]>([]);
  const templateFormattersRef = React.useRef<string[]>([]);
  const templateEntitiesRef = React.useRef<string[]>([]);
  const templateAttributesByEntityRef = React.useRef<Map<string, string[]>>(new Map());
  const [error, setError] = React.useState("");
  const [popoverEl, setPopoverEl] = React.useState<HTMLDivElement | null>(null);
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);

  // Reset uncontrolled value when resetKey changes
  React.useEffect(() => {
    setUncontrolledValue(defaultValue);
  }, [resetKey, defaultValue]);

  const field = useField<string>({
    name: name ?? "__json_editor_internal",
    value: propValue,
    onValueChange: onChange,
  });

  const graphTemplateEntries = React.useMemo<Array<{ value: string; entity?: string }>>(() => {
    if (!graph) return [];

    try {
      if (graph instanceof DecisionGraph) {
        return graph.ids().map((id) => {
          const nodeId = graph.deref(id);
          const description = graph.field(id, "description");
          const ownId = graph.field(id, "id");
          const value = useDescription ? description ?? ownId ?? nodeId : ownId ?? description ?? nodeId;
          const entity = graph.field(id, "entity");
          return { value: typeof value === "string" ? value : "", entity: typeof entity === "string" ? entity : undefined };
        });
      }

      if (Array.isArray(graph.nodes)) {
        return graph.nodes.map((node: any) => {
          const value = useDescription ? node?.description ?? node?.id : node?.id ?? node?.description;
          const entity = typeof node?.entity === "string" ? node.entity : undefined;
          return { value: typeof value === "string" ? value : "", entity };
        });
      }
    } catch {
      // Ignore graph parsing failures and fall back to explicit attributes only.
    }

    return [];
  }, [graph, useDescription]);

  const graphTemplateAttributes = React.useMemo(
    () => normalizeUniqueStrings(graphTemplateEntries.map((entry) => entry.value)),
    [graphTemplateEntries],
  );
  const graphTemplateEntities = React.useMemo(
    () => normalizeUniqueStrings(graphTemplateEntries.map((entry) => entry.entity ?? "")),
    [graphTemplateEntries],
  );
  const graphTemplateAttributesByEntity = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const entry of graphTemplateEntries) {
      if (!entry.entity || !entry.value) continue;
      const existing = map.get(entry.entity) ?? [];
      if (!existing.includes(entry.value)) {
        existing.push(entry.value);
        map.set(entry.entity, existing);
      }
    }
    return map;
  }, [graphTemplateEntries]);

  const resolvedTemplateAttributes = React.useMemo(
    () => normalizeUniqueStrings((templateAttributes?.length ? templateAttributes : graphTemplateAttributes) ?? []),
    [templateAttributes, graphTemplateAttributes],
  );
  const resolvedTemplateFormatters = React.useMemo(
    () => normalizeUniqueStrings(templateFormatters?.length ? templateFormatters : DEFAULT_TEMPLATE_FORMATTERS),
    [templateFormatters],
  );
  const resolvedTemplateEntities = React.useMemo(
    () => normalizeUniqueStrings(templateEntities?.length ? templateEntities : graphTemplateEntities),
    [templateEntities, graphTemplateEntities],
  );

  React.useEffect(() => {
    templateAwareRef.current = templateAware;
  }, [templateAware]);

  React.useEffect(() => {
    templateAttributesRef.current = resolvedTemplateAttributes;
  }, [resolvedTemplateAttributes]);

  React.useEffect(() => {
    templateFormattersRef.current = resolvedTemplateFormatters;
  }, [resolvedTemplateFormatters]);

  React.useEffect(() => {
    templateEntitiesRef.current = resolvedTemplateEntities;
  }, [resolvedTemplateEntities]);

  React.useEffect(() => {
    templateAttributesByEntityRef.current = graphTemplateAttributesByEntity;
  }, [graphTemplateAttributesByEntity]);

  let value = field.value ?? uncontrolledValue;

  if (uncontrolledValue && value) {
    // if they both are the same JSON use the uncontrolled one so whitespace changes are preserved
    try {
      if (JSON.stringify(JSON.parse(uncontrolledValue)) === JSON.stringify(JSON.parse(value))) {
        //console.log(value, uncontrolledValue);
        value = uncontrolledValue;
      }
    } catch { }
  }

  const setErrAndPop = React.useCallback((v: string) => {
    setError(v);
    const el = buttonRef.current?.parentElement;
    setPopoverEl(el instanceof HTMLDivElement ? el : null);
  }, []);

  const clearError = React.useCallback(() => setError(""), []);
  const handleChange = React.useCallback((v: string) => {
    setUncontrolledValue(v);
    field.setValue(v, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    onChange?.(v);
  }, [field, onChange]);

  const syncTemplateMarkers = React.useCallback((val: string) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel?.();
    if (!editor || !monaco || !model) return;

    if (!templateAwareRef.current) {
      monaco.editor.setModelMarkers(model, TEMPLATE_MARKER_OWNER, []);
      return;
    }

    const diagnostics = findTemplateDiagnostics(
      val,
      templateAttributesRef.current,
      templateEntitiesRef.current,
      templateAttributesByEntityRef.current,
    );
    const markers = diagnostics.map((diagnostic) => {
      const start = model.getPositionAt(diagnostic.startOffset);
      const end = model.getPositionAt(diagnostic.endOffset);
      return {
        startLineNumber: start.lineNumber,
        startColumn: start.column,
        endLineNumber: end.lineNumber,
        endColumn: end.column,
        message: diagnostic.message,
        severity: monaco.MarkerSeverity.Error,
      };
    });

    monaco.editor.setModelMarkers(model, TEMPLATE_MARKER_OWNER, markers);
  }, []);

  const revealJsonPath = React.useCallback((path: string | undefined) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel?.();
    if (!editor || !monaco || !model) return;
    guidanceDecorationsRef.current = editor.deltaDecorations(guidanceDecorationsRef.current, []);
    if (path === undefined) return;
    const lineNumber = getJsonPropertyLine(model.getValue(), path);
    const lineLength = model.getLineLength(lineNumber);
    guidanceDecorationsRef.current = editor.deltaDecorations([], [{
      range: new monaco.Range(lineNumber, 1, lineNumber, lineLength + 1),
      options: { isWholeLine: true, className: "adapter-json-guidance-line" },
    }]);
    editor.revealLineInCenter(lineNumber);
    editor.setPosition({ lineNumber, column: Math.min(lineLength + 1, 3) });
    editor.focus();
  }, []);

  React.useEffect(() => {
    revealPathRef.current = revealPath;
    revealJsonPath(revealPath);
  }, [revealJsonPath, revealPath]);

  const beautify = React.useCallback(() => {
    try {
      const parsed = JSON.parse(value);
      handleChange(JSON.stringify(parsed, null, 2));
    } catch (e) {
      if (!(e instanceof Error)) throw e;
      const position = e.message.match(/position (\d+)/);
      const meaningfulError = e.message.match(/^([\s\S]+) in JSON/);
      if (position === null || meaningfulError === null) {
        setErrAndPop(e.message);
        return;
      }
      const n = Number(position[1]);
      const errorLineNumber = value.split("").reduce((a, it, i) => (i >= n || it !== "\n" ? a : a + 1), 1);
      setErrAndPop(`${meaningfulError[1]} on line ${errorLineNumber}`);
    }
  }, [value, handleChange, setErrAndPop]);


  const handleEditorChange = React.useCallback((newValue: string | undefined) => {
    const val = newValue ?? "";
    let errMsg: string | undefined;

    try { JSON.parse(val); } catch (e: any) { errMsg = e.message || "Invalid JSON"; }

    if (!errMsg && templateAwareRef.current) {
      const diagnostics = findTemplateDiagnostics(
        val,
        templateAttributesRef.current,
        templateEntitiesRef.current,
        templateAttributesByEntityRef.current,
      );
      const [firstTemplateError] = diagnostics;
      if (firstTemplateError) {
        const model = editorRef.current?.getModel?.();
        if (model) {
          const pos = model.getPositionAt(firstTemplateError.startOffset);
          errMsg = `${firstTemplateError.message} (line ${pos.lineNumber})`;
        } else {
          errMsg = firstTemplateError.message;
        }
      }
    }

    handleChange?.(val);
    syncTemplateMarkers(val);
  }, [handleChange, syncTemplateMarkers]);

  const registerTemplateCompletionProvider = React.useCallback(() => {
    const monaco = monacoRef.current;
    completionProviderRef.current?.dispose();
    completionProviderRef.current = null;
    if (!templateAwareRef.current || !monaco) return;

    completionProviderRef.current = monaco.languages.registerCompletionItemProvider("json", {
      triggerCharacters: ["{", "|"],
      provideCompletionItems: (model: any, position: any) => {
        const currentModel = editorRef.current?.getModel?.();
        if (!templateAwareRef.current || !currentModel || currentModel.uri.toString() !== model.uri.toString()) {
          return { suggestions: [] };
        }

        const text = model.getValue();
        const offset = model.getOffsetAt(position);
        const context = getTemplateCompletionContext(text, offset);
        if (!context) return { suggestions: [] };

        const sourceItems =
          context.mode === "attribute"
            ? getTemplateAttributeSuggestions({
                searchTerm: context.searchTerm,
                attributes: templateAttributesRef.current,
                entities: templateEntitiesRef.current,
                attributesByEntity: templateAttributesByEntityRef.current,
              })
            : templateFormattersRef.current
                .filter((item) => item.toLowerCase().includes(context.searchTerm.toLowerCase()))
                .sort((a, b) => {
                  const scoreA = scoreSuggestion(a, context.searchTerm);
                  const scoreB = scoreSuggestion(b, context.searchTerm);
                  if (scoreA !== scoreB) return scoreA - scoreB;
                  return a.localeCompare(b);
                });
        const ordered = sourceItems.slice(0, 40);

        if (!ordered.length) return { suggestions: [] };

        const start = model.getPositionAt(context.replaceStart);
        const range = {
          startLineNumber: start.lineNumber,
          startColumn: start.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };

        const suggestions = ordered.map((item) => ({
          label: item,
          kind:
            context.mode === "attribute"
              ? monaco.languages.CompletionItemKind.Variable
              : monaco.languages.CompletionItemKind.Function,
          insertText: item,
          range,
          detail: context.mode === "attribute" ? "Template attribute" : "Template formatter",
        }));

        return { suggestions };
      },
    });
  }, []);

  const handleEditorMount = React.useCallback((editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    registerTemplateCompletionProvider();

    syncTemplateMarkers(editor.getValue?.() ?? "");
    revealJsonPath(revealPathRef.current);
  }, [registerTemplateCompletionProvider, revealJsonPath, syncTemplateMarkers]);

  React.useEffect(() => {
    registerTemplateCompletionProvider();
  }, [templateAware, registerTemplateCompletionProvider]);

  React.useEffect(() => {
    if (typeof value === "string") {
      syncTemplateMarkers(value);
    }
  }, [value, templateAware, resolvedTemplateAttributes, resolvedTemplateEntities, graphTemplateAttributesByEntity, syncTemplateMarkers]);

  React.useEffect(() => () => {
    completionProviderRef.current?.dispose();
    const model = editorRef.current?.getModel?.();
    if (model && monacoRef.current) {
      monacoRef.current.editor.setModelMarkers(model, TEMPLATE_MARKER_OWNER, []);
    }
    guidanceDecorationsRef.current = editorRef.current?.deltaDecorations?.(guidanceDecorationsRef.current, []) ?? [];
  }, []);

  return (
    <Wrap className={className}>
      <Toolbar>
        {withCopy && <CopyToCbdBtn v={value} withBeautify={Boolean(onChange)} />}
        <Tooltip title={t("beautify")} placement="left">
          <StyledIconButton
            label="Beautify JSON"
            onClick={beautify}
            ref={buttonRef}
            className={onChange === undefined ? "hidden" : undefined}
          >
            <Icon name="code" />
          </StyledIconButton>
        </Tooltip>
      </Toolbar>
      <StyledPopover
        open={Boolean(error)}
        anchorEl={popoverEl}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {error}
      </StyledPopover>
      <InnerWrap>
        <MonacoEditor
          height="100%"
          defaultLanguage="json"
          value={value}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          options={{
            ...BASE_EDITOR_OPTIONS,
            readOnly: readOnly || disabled,
            quickSuggestions: templateAware ? { strings: true, comments: false, other: false } : false,
            parameterHints: { enabled: false },
            suggestOnTriggerCharacters: Boolean(templateAware),
            acceptSuggestionOnEnter: templateAware ? "on" : "off",
            tabCompletion: templateAware ? "on" : "off",
          }}
        />
      </InnerWrap>
    </Wrap>
  );
});

JsonEditor.displayName = "common/JsonEditor";

export interface JsonDiffEditorProps {
  /** JSON before the change (left). */
  original: string;
  /** JSON after the change (right). */
  modified: string;
  className?: string;
  /** Show a copy button, which copies `copyValue` (the modified JSON unless given). */
  withCopy?: boolean;
  copyValue?: string;
}

/** A read-only side-by-side JSON diff, styled like JsonEditor. */
export const JsonDiffEditor: React.FC<JsonDiffEditorProps> = React.memo(
  ({ original, modified, className, withCopy, copyValue }) => (
    <Wrap className={className}>
      {withCopy && (
        <Toolbar>
          <CopyToCbdBtn v={copyValue ?? modified} />
        </Toolbar>
      )}
      <InnerWrap>
        <DiffEditor
          height="100%"
          language="json"
          original={original}
          modified={modified}
          options={{ ...BASE_EDITOR_OPTIONS, readOnly: true, originalEditable: false, renderSideBySide: true }}
        />
      </InnerWrap>
    </Wrap>
  ),
);

JsonDiffEditor.displayName = "common/JsonDiffEditor";
